'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  href: string;
  description?: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
  { label: 'Home', href: '/homepage', description: 'Mission Control Center' },
  { label: 'Capabilities', href: '/capabilities', description: 'Manufacturing Excellence' },
  { label: 'Explore Solutions', href: '/explore-solutions', description: 'Capability Matching' },
  { label: 'Client Portal', href: '/client-portal', description: 'Project Command' },
  { label: 'Resources', href: '/resources', description: 'Technical Library' }];


  const secondaryItems: NavigationItem[] = [
  { label: 'Company Deck', href: '/company-deck' },
  { label: 'Support', href: '/support' },
  { label: 'About', href: '/about' },
  { label: 'Admin', href: '/admin/partnerships' }];


  const certifications = [
  { name: 'AIRBUS-APPROVED', icon: 'ShieldCheckIcon' },
  { name: 'SECURED DATA SYSTEMS', icon: 'LockClosedIcon' }];


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-card shadow-aerospace ${className}`}>
      <div className="mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo Section */}
          <Link href="/homepage" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-32 h-32 flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                <img
                  src="/assets/images/Screenshot_2026-06-28_at_11.22.51_AM-1782626245471.png"
                  alt="Wings Bharath Logo"
                  className="w-32 h-32 object-contain" />

              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse-glow"></div>
            </div>
            <div className="flex flex-col">
              <span className="font-accent font-bold text-foreground tracking-wide text-[25px]">WINGS BHARATH

              </span>
              <span className="font-cta text-xs tracking-wider text-[rgba(102,252,255,1)]">AI INTEGRATED MANUFACTURING

              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              className="group relative px-4 py-2 rounded-lg transition-all duration-300 hover:bg-muted">

                <span className="font-body text-sm font-medium text-foreground group-hover:text-accent transition-colors duration-300">
                  {item.label}
                </span>
                {item.description &&
              <span className="absolute left-0 right-0 -bottom-8 text-xs text-technical-gray opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                    {item.description}
                  </span>
              }
              </Link>
            )}
          </nav>

          {/* Desktop Right Section */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Certifications */}
            <div className="flex items-center space-x-3">
              {certifications.map((cert) =>
              <div
                key={cert.name}
                className="flex items-center space-x-1 px-3 py-1.5 bg-muted rounded-md border border-border hover:border-accent transition-all duration-300 hover:shadow-glow group">

                  <Icon
                  name={cert.icon as any}
                  size={16}
                  className="text-aerospace-green group-hover:text-accent transition-colors duration-300" />

                  <span className="font-cta text-xs text-technical-gray group-hover:text-accent transition-colors duration-300">
                    {cert.name}
                  </span>
                </div>
              )}
            </div>

            {/* Secondary Navigation */}
            <div className="flex items-center space-x-2">
              {secondaryItems.map((item) =>
              <Link
                key={item.href}
                href={item.href}
                className="px-3 py-2 text-sm font-body text-technical-gray hover:text-accent transition-colors duration-300">

                  {item.label}
                </Link>
              )}
            </div>

            {/* CTA Button */}
            <div className="flex items-center space-x-2">
              <Link
                href="/rfq"
                className="group relative px-6 py-2.5 font-cta text-sm font-medium rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.08) 0%, rgba(238,149,114,0.08) 100%)',
                  border: '1px solid transparent',
                  backgroundClip: 'padding-box',
                }}
              >
                {/* Animated gradient border */}
                <span className="absolute inset-0 rounded-lg p-px" style={{
                  background: 'linear-gradient(135deg, #00d4ff, #ee9572, #00d4ff)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                }} />
                {/* Scan line */}
                <span className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
                  <span className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent"
                    style={{ animation: 'rfqScan 2.5s ease-in-out infinite' }} />
                </span>
                {/* Glow on hover */}
                <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ boxShadow: '0 0 20px rgba(0,212,255,0.35), inset 0 0 20px rgba(0,212,255,0.05)' }} />
                <span className="relative z-10 flex items-center space-x-2">
                  {/* Pulse dot */}
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                  </span>
                  <Icon name="DocumentTextIcon" size={15} className="text-accent group-hover:text-foreground transition-colors duration-300" />
                  <span className="text-accent group-hover:text-foreground transition-colors duration-300 tracking-widest">RFQ</span>
                  <span className="text-accent/50 font-mono text-[10px] group-hover:text-foreground/60 transition-colors duration-300">▶</span>
                </span>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg bg-muted hover:bg-accent/10 transition-colors duration-300"
            aria-label="Toggle mobile menu">

            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              className="text-foreground" />

          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen &&
      <div className="lg:hidden bg-card border-t border-border animate-slide-in-left">
          <div className="px-6 py-4 space-y-4">
            {/* Mobile Navigation Items */}
            <div className="space-y-2">
              {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-lg bg-muted hover:bg-accent/10 transition-colors duration-300">

                  <span className="font-body text-sm font-medium text-foreground">
                    {item.label}
                  </span>
                  {item.description &&
              <span className="block text-xs text-technical-gray mt-1">
                      {item.description}
                    </span>
              }
                </Link>
            )}
            </div>

            {/* Mobile Secondary Items */}
            <div className="pt-4 border-t border-border space-y-2">
              {secondaryItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-2 text-sm font-body text-technical-gray hover:text-accent transition-colors duration-300">

                  {item.label}
                </Link>
            )}
            </div>

            {/* Mobile Certifications */}
            <div className="flex items-center justify-center space-x-3 pt-4 border-t border-border">
              {certifications.map((cert) =>
            <div
              key={cert.name}
              className="flex items-center space-x-1 px-3 py-1.5 bg-muted rounded-md border border-border">

                  <Icon
                name={cert.icon as any}
                size={16}
                className="text-aerospace-green" />

                  <span className="font-cta text-xs text-technical-gray">
                    {cert.name}
                  </span>
                </div>
            )}
            </div>

            {/* Mobile CTA */}
            <div className="flex flex-col gap-2">
              <Link
                href="/rfq"
                onClick={() => setIsMobileMenuOpen(false)}
                className="group relative block w-full px-6 py-3 font-cta text-sm font-medium text-center rounded-lg overflow-hidden transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.08) 0%, rgba(238,149,114,0.08) 100%)',
                  border: '1px solid rgba(0,212,255,0.4)',
                }}
              >
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                  </span>
                  <Icon name="DocumentTextIcon" size={15} className="text-accent" />
                  <span className="text-accent tracking-widest">REQUEST FOR QUOTATION</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      }

      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes rfqScan {
          0% { top: -2px; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </header>);

};

export default Header;